import styled from "@emotion/styled";
import { Headline6, Body2 } from "@material/react-typography";
import React, { useState, useEffect } from "react";
import { CompanyRole, RoleAssignment } from "./types";
import { ClientForm } from "~/components/forms/ClientForm";
import { EmployeePicker } from "~/visuals/organisms/EmployeePickerFormField";
import { ValueRenderer } from "~/components/forms/ValueContext";
import Updater from "~/components/forms/Updater";
import Spinner from "~/components/spinner";
import { useAddRole } from "./useAddRole";
import { Employee } from "~/gql/types";
import _ from "lodash";
import Button from "~/components/button";
import { formatFirstMiddleLast } from "~/utils/formatEmployeeName";

type EmployeeRolePaneProps = {
  selectedRole: RoleAssignment | null;
  removeRole: (role: CompanyRole, userPrincipalName: string) => Promise<void>;
};

const Wrapper = styled("div")`
  background-color: #fff;
  padding: 8px;
  border: 1px solid rgba(0, 0, 0, 0.42);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EmployeeList = styled("div")`
  height: 100%;
  overflow: scroll;
`;

const RoleHeader = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0;
  padding-bottom: 8px;
  min-height: 64px;
  gap: 16px;

  & > h6.mdc-typography {
    margin: 0;
  }
`;

const RolePlaceholderCaption = styled(Headline6)`
  &.mdc-typography--headline6 {
    color: gray;
    margin-top: 16px;
  }
`;

const EmployeeRow = styled("div")`
  grid-template-columns: 1fr 64px;
  display: grid;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.4);

  & .material-icons {
    color: var(--mdc-theme-secondary);
  }
`;

export const EmployeeRolePane: React.FC<EmployeeRolePaneProps> = ({
  selectedRole,
  removeRole,
}) => {
  const [addEmployee, setAddEmployee] = useState(false);

  useEffect(() => {
    setAddEmployee(false);
  }, [selectedRole]);

  if (selectedRole === null) {
    return (
      <Wrapper>
        <RolePlaceholderCaption>(No role selected)</RolePlaceholderCaption>
      </Wrapper>
    );
  }

  const sortedEmployees = _.sortBy(selectedRole.employees, [
    (x) => x.lastName,
    (x) => x.firstName,
  ]);

  return (
    <Wrapper>
      <RoleHeader>
        <Headline6>Role: {selectedRole.roleName}</Headline6>
        {!addEmployee && (
          <Button icon="add" onClick={() => setAddEmployee(true)}>
            Add Employee
          </Button>
        )}
        {addEmployee && (
          <AddEmployeeComponent
            role={selectedRole}
            setAddEmployee={setAddEmployee}
          />
        )}
      </RoleHeader>
      <EmployeeList>
        {sortedEmployees.map((emp, idx) => (
          <EmployeeRow key={idx}>
            <div>
              <Body2>{formatFirstMiddleLast(emp)}</Body2>
            </div>

            <Button
              icon="delete"
              onClick={() =>
                removeRole(selectedRole.roleName, emp.userPrincipalName)
              }
            />
          </EmployeeRow>
        ))}
      </EmployeeList>
    </Wrapper>
  );
};

// removes side margins to make spacing more consistent in the Employee Role Pane
// removes empty text field helper line element to help bottom spacing
const AddEmployeeComponentWrapper = styled("div")`
  & .picker.form-field {
    margin: 8px 0;
  }
  & .mdc-text-field-helper-line {
    display: none;
  }
`;

type AddEmployeeComponentProps = {
  role: RoleAssignment;
  setAddEmployee: React.Dispatch<boolean>;
};

export const AddEmployeeComponent: React.FC<AddEmployeeComponentProps> = ({
  role,
  setAddEmployee,
}) => {
  const { roleName, employees } = role;
  const { addRole, loading } = useAddRole(roleName);

  const [employee, setEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    if (
      employee &&
      !employees.find(
        (x) =>
          x.userPrincipalName.toLowerCase() ==
          employee.userPrincipalName.toLowerCase()
      )
    ) {
      void addRole(employee);
    }
  }, [employee]);

  return (
    <AddEmployeeComponentWrapper>
      <ClientForm
        onSuccess={() => undefined}
        initialValues={{ employee: null }}
      >
        <EmployeePicker
          formField={"employee"}
          label={"Employee"}
          helperText={""}
        />
        <ValueRenderer
          render={(values) => (
            <Updater value={values.employee} callback={setEmployee} />
          )}
        />
        <Spinner open={loading} />
      </ClientForm>
      <Button onClick={() => setAddEmployee(false)}>Cancel</Button>
    </AddEmployeeComponentWrapper>
  );
};
